import type { TFunction } from "i18next";
import React from "react";
import ReactTooltip from "react-tooltip";
import { Link } from "react-router-dom";
import type { ObjectReferenceResult } from "../../types/types.PIM";
import { ObjectReferencePreviewTooltip } from "../ObjectReference/ObjectReferencePreviewTooltip";
import { buildObjectRefPreviewDisplayRows } from "../ObjectReference/utils";

export type MultiObjectReferenceTooltipProps = {
  references: ObjectReferenceResult[];
  isAccessible: (ref: ObjectReferenceResult) => boolean;
  getHref: (ref: ObjectReferenceResult) => string | null;
  getRestrictedTooltip: (ref: ObjectReferenceResult) => string;
  t: TFunction;
  onMoreClick?: () => void;
};

export function MultiObjectReferenceTooltip({
  references,
  isAccessible,
  getHref,
  getRestrictedTooltip,
  t,
  onMoreClick,
}: MultiObjectReferenceTooltipProps): React.ReactElement {
  const remainingCount = references.length - 1;
  const firstRef = references[0];
  const firstRefAccessible = firstRef ? isAccessible(firstRef) : false;
  const firstRefHref = firstRef ? getHref(firstRef) : null;
  const firstRefPreviewRows = firstRef
    ? buildObjectRefPreviewDisplayRows(firstRef, t)
    : [];
  const firstRefPreviewAttrs = firstRefPreviewRows.map((r) => ({
    label: r.label,
    value: r.value,
  }));

  const firstRefContent = firstRefAccessible ? (
    <ObjectReferencePreviewTooltip
      tooltipId={`object-ref-${firstRef?.id}-multi-first`}
      previewAttributes={firstRefPreviewAttrs}
    >
      {firstRefHref ? (
        <Link to={firstRefHref}>{firstRef?.name ?? ""}</Link>
      ) : (
        <span>{firstRef?.name ?? ""}</span>
      )}
    </ObjectReferencePreviewTooltip>
  ) : firstRef ? (
    <>
      <span data-tip={getRestrictedTooltip(firstRef)} data-for={firstRef.id}>
        {firstRef.name}
      </span>
      <ReactTooltip delayHide={200} id={firstRef.id} effect="solid" />
    </>
  ) : (
    <span />
  );

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        maxWidth: "100%",
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {firstRefContent}
      </span>
      {remainingCount > 0 && (
        <span
          onClick={onMoreClick}
          style={{
            whiteSpace: "nowrap",
            flexShrink: 0,
            cursor: onMoreClick ? "pointer" : "default",
          }}
        >
          {`+${remainingCount} ${t("more")}`}
        </span>
      )}
    </span>
  );
}
